import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Lab } from '../../../model';

/**
 * Tiny colour swatch for a CIE-Lab colour.
 *
 * - `lab` is `null` / `undefined` → renders nothing.
 * - otherwise → a small square filled with the `lab()` CSS colour; hovering (or focusing) shows the
 *   numeric L/A/B values, and screen readers get the same via `aria-label`.
 *
 * Standalone — import it directly (`imports: [LabColorPreviewComponent]`).
 *
 * @deprecated Use {@link FormulationColorPreviewComponent} (`app-formulation-color-preview`) instead.
 *   It shows the same LAB swatch + tooltip AND adds the colorant formulation when a {@link Color} is
 *   provided. Pass `[color]="color"` instead of `[lab]="color?.lab"`.
 */
@Component({
  selector: 'app-lab-color-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzTooltipModule],
  template: `
    @if (lab(); as l) {
      <span
        class="swatch"
        role="img"
        [attr.aria-label]="ariaLabel()"
        [style.background]="cssColor()"
        nz-tooltip
        [nzTooltipTitle]="ariaLabel()"></span>
    }
  `,
  styles: [`
    .swatch {
      display: inline-block;
      width: 16px;
      height: 16px;
      border-radius: 3px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      vertical-align: middle;
      cursor: help;
    }
  `],
})
export class LabColorPreviewComponent {
  /** The CIE-Lab colour to preview. `null` / `undefined` ⇒ the component renders nothing. */
  readonly lab = input<Lab | null | undefined>(undefined);

  /** `lab()` CSS colour string for the swatch background (empty when there is nothing to show). */
  readonly cssColor = computed((): string => {
    const l = this.lab();
    return l ? `lab(${l.l} ${l.a} ${l.b})` : '';
  });

  /** Human-readable L/A/B for the tooltip and the accessible label. */
  readonly ariaLabel = computed((): string => {
    const l = this.lab();
    return l ? `LAB — L: ${l.l.toFixed(1)}, A: ${l.a.toFixed(1)}, B: ${l.b.toFixed(1)}` : '';
  });
}
